import React, { useEffect, useState } from "react";
import {
  Save,
  Loader2,
  Plus,
  Trash2,
  RotateCcw,
  ExternalLink,
  Image as ImageIcon,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { adminApi } from "./api";
import { useContent } from "../contexts/ContentContext";
import { useToast } from "../hooks/use-toast";

// ============================================================================
// Schema defines which fields appear in the editor for each section.
// type: text | textarea | image | number
// ============================================================================
const SECTIONS = [
  {
    key: "company",
    title: "Company Info",
    desc: "Brand name, contact details, and address shown in navbar & footer.",
    kind: "object",
    fields: [
      { key: "name", label: "Company name", type: "text" },
      { key: "tagline", label: "Tagline", type: "text" },
      { key: "logoText", label: "Logo - main text", type: "text" },
      { key: "logoSubText", label: "Logo - sub text", type: "text" },
      { key: "phone", label: "Phone", type: "text" },
      { key: "email", label: "Email", type: "text" },
      { key: "whatsapp", label: "WhatsApp", type: "text" },
      { key: "address", label: "Address", type: "textarea" },
      { key: "established", label: "Established (year)", type: "text" },
      { key: "installations", label: "Installations stat", type: "text" },
      { key: "cities", label: "Cities stat", type: "text" },
      { key: "capacity", label: "Capacity stat", type: "text" },
    ],
  },
  {
    key: "heroSlides",
    title: "Hero Slides",
    desc: "Top banner carousel slides.",
    kind: "array",
    itemLabel: (it) => it?.title || "Slide",
    fields: [
      { key: "eyebrow", label: "Eyebrow tag", type: "text" },
      { key: "eyebrowNote", label: "Eyebrow note", type: "text" },
      { key: "title", label: "Title (orange part)", type: "text" },
      { key: "titleAccent", label: "Subtitle (white part)", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "cta", label: "Button text", type: "text" },
      { key: "image", label: "Image URL", type: "image" },
    ],
  },
  {
    key: "trustBadges",
    title: "Trust Stats",
    desc: "Four small stat cards under the hero.",
    kind: "array",
    itemLabel: (it) => it?.label || "Stat",
    fields: [
      { key: "value", label: "Value (e.g. 5,000+)", type: "text" },
      { key: "label", label: "Label", type: "text" },
    ],
  },
  {
    key: "aboutSection",
    title: "About Section",
    desc: "The 'Know your Solar Experts' section.",
    kind: "object",
    fields: [
      { key: "eyebrow", label: "Eyebrow tag", type: "text" },
      { key: "title", label: "Title", type: "text" },
      { key: "titleAccent", label: "Title accent (orange)", type: "text" },
      { key: "image", label: "Image URL", type: "image" },
    ],
    arrays: [
      { key: "paragraphs", label: "Paragraphs", placeholder: "Paragraph text", multiline: true },
      { key: "highlights", label: "Highlights (bullet points)", placeholder: "Highlight" },
    ],
  },
  {
    key: "services",
    title: "Solutions We Deliver",
    desc: "The grid of solutions/services you offer (C&I, OPEX, Residential, EV, ESS, etc.).",
    kind: "array",
    itemLabel: (it) => it?.title || "Solution",
    fields: [
      { key: "icon", label: "Icon (lucide-react name)", type: "text", hint: "e.g. Factory, Home, Plug, BatteryFull, Leaf, Sun, Wrench" },
      { key: "title", label: "Title", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "image", label: "Image URL", type: "image" },
    ],
  },
  {
    key: "industries",
    title: "Industries We Serve",
    desc: "Sectors and verticals your solutions cater to.",
    kind: "array",
    itemLabel: (it) => it?.name || "Industry",
    fields: [
      { key: "icon", label: "Icon (lucide-react name)", type: "text", hint: "e.g. Hospital, School, Factory, Train, Server, Car" },
      { key: "name", label: "Industry name", type: "text" },
    ],
  },
  {
    key: "whyChooseUs",
    title: "Why Choose Us",
    desc: "Hexagon feature tiles.",
    kind: "array",
    itemLabel: (it) => it?.title || "Feature",
    fields: [
      { key: "icon", label: "Icon", type: "text", hint: "BadgePercent, Wallet, FileCheck2, Compass, Headset, Settings" },
      { key: "title", label: "Title", type: "text" },
      { key: "text", label: "Text", type: "textarea" },
    ],
  },
  {
    key: "processSteps",
    title: "Process Steps",
    desc: "The how-it-works timeline.",
    kind: "array",
    itemLabel: (it) => `${it?.step || ""} ${it?.title || "Step"}`,
    fields: [
      { key: "step", label: "Step number", type: "text" },
      { key: "title", label: "Title", type: "text" },
      { key: "text", label: "Description", type: "textarea" },
    ],
  },
  {
    key: "projects",
    title: "Projects",
    desc: "Project gallery cards.",
    kind: "array",
    itemLabel: (it) => it?.name || "Project",
    fields: [
      { key: "name", label: "Customer name", type: "text" },
      { key: "location", label: "Location", type: "text" },
      { key: "capacity", label: "Capacity (e.g. 5 kW)", type: "text" },
      { key: "image", label: "Image URL", type: "image" },
    ],
  },
  {
    key: "testimonials",
    title: "Testimonials",
    desc: "Customer review cards.",
    kind: "array",
    itemLabel: (it) => it?.name || "Testimonial",
    fields: [
      { key: "name", label: "Customer name", type: "text" },
      { key: "location", label: "Location", type: "text" },
      { key: "rating", label: "Rating (1-5)", type: "number" },
      { key: "text", label: "Quote", type: "textarea" },
      { key: "image", label: "Photo URL", type: "image" },
    ],
  },
  {
    key: "faqs",
    title: "FAQs",
    desc: "Frequently asked questions.",
    kind: "array",
    itemLabel: (it) => it?.q || "Question",
    fields: [
      { key: "q", label: "Question", type: "text" },
      { key: "a", label: "Answer", type: "textarea" },
    ],
  },
  {
    key: "socials",
    title: "Social Media",
    desc: "Social media links shown in the footer.",
    kind: "array",
    itemLabel: (it) => it?.name || "Social",
    fields: [
      { key: "name", label: "Platform name", type: "text" },
      { key: "icon", label: "Icon (lucide-react name)", type: "text", hint: "Facebook, Instagram, Youtube, Linkedin, Twitter" },
      { key: "href", label: "Profile URL", type: "text" },
    ],
  },
  {
    key: "legalPages",
    title: "Legal Pages",
    desc: "Privacy Policy, Terms of Service, and Refund Policy content.",
    kind: "legalPages",
  },
  {
    key: "blogPosts",
    title: "Blog Posts",
    desc: "Articles for your /blog page.",
    kind: "array",
    itemLabel: (it) => it?.title || "Post",
    fields: [
      { key: "slug", label: "URL slug (e.g. pm-surya-ghar-guide)", type: "text" },
      { key: "title", label: "Title", type: "text" },
      { key: "excerpt", label: "Short excerpt", type: "textarea" },
      { key: "cover", label: "Cover image URL", type: "image" },
      { key: "category", label: "Category", type: "text" },
      { key: "author", label: "Author", type: "text" },
      { key: "publishedAt", label: "Published date (YYYY-MM-DD)", type: "text" },
      { key: "content", label: "Article content (supports ## headings, - bullets, blank lines)", type: "longtext" },
    ],
  },
];

// ============================================================================
// Field renderers
// ============================================================================
function FieldInput({ field, value, onChange }) {
  if (field.type === "textarea") {
    return (
      <Textarea
        rows={3}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.label}
      />
    );
  }
  if (field.type === "longtext") {
    return (
      <Textarea
        rows={14}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.label}
        className="font-mono text-[13px] leading-relaxed"
      />
    );
  }
  if (field.type === "image") {
    return (
      <div className="space-y-2">
        <Input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="h-10"
        />
        {value ? (
          <div className="flex items-center gap-3">
            <img src={value} alt="" className="w-20 h-14 object-cover rounded border" />
            <a href={value} target="_blank" rel="noreferrer" className="text-xs text-[#0a4f47] inline-flex items-center gap-1 hover:underline">
              Open <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <ImageIcon className="w-4 h-4" /> No image set
          </div>
        )}
      </div>
    );
  }
  return (
    <Input
      type={field.type === "number" ? "number" : "text"}
      value={value ?? ""}
      onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)}
      placeholder={field.label}
      className="h-10"
    />
  );
}

function ObjectEditor({ section, value, onChange }) {
  const v = value || {};
  const update = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        {section.fields.map((f) => (
          <div key={f.key} className={f.type === "textarea" || f.type === "image" ? "md:col-span-2" : ""}>
            <Label className="text-sm font-semibold text-slate-700">{f.label}</Label>
            {f.hint && <div className="text-xs text-slate-400 mb-1">{f.hint}</div>}
            <div className="mt-1.5">
              <FieldInput field={f} value={v[f.key]} onChange={(val) => update(f.key, val)} />
            </div>
          </div>
        ))}
      </div>
      {section.arrays?.map((arr) => (
        <SimpleStringList
          key={arr.key}
          label={arr.label}
          placeholder={arr.placeholder}
          multiline={arr.multiline}
          value={v[arr.key] || []}
          onChange={(items) => update(arr.key, items)}
        />
      ))}
    </div>
  );
}

function SimpleStringList({ label, value, onChange, placeholder, multiline }) {
  const update = (i, newVal) => {
    const copy = [...value];
    copy[i] = newVal;
    onChange(copy);
  };
  const add = () => onChange([...value, ""]);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div>
      <Label className="text-sm font-semibold text-slate-700">{label}</Label>
      <div className="mt-2 space-y-2">
        {value.map((s, i) => (
          <div key={i} className="flex items-start gap-2">
            {multiline ? (
              <Textarea
                rows={2}
                value={s}
                onChange={(e) => update(i, e.target.value)}
                placeholder={placeholder}
              />
            ) : (
              <Input
                value={s}
                onChange={(e) => update(i, e.target.value)}
                placeholder={placeholder}
                className="h-10"
              />
            )}
            <Button type="button" variant="ghost" size="icon" className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0" onClick={() => remove(i)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" className="gap-1" onClick={add}>
          <Plus className="w-4 h-4" /> Add
        </Button>
      </div>
    </div>
  );
}

function LegalPagesEditor({ value, onChange }) {
  const pages = [
    { key: "privacy", label: "Privacy Policy" },
    { key: "terms", label: "Terms of Service" },
    { key: "refund", label: "Refund Policy" },
  ];
  const [active, setActive] = React.useState("privacy");
  const current = value?.[active] || { title: "", lastUpdated: "", content: "" };

  const updatePage = (patch) => {
    onChange({ ...value, [active]: { ...current, ...patch } });
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        {pages.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setActive(p.key)}
            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
              active === p.key
                ? "bg-[#0a4f47] text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-semibold text-slate-700">Page title</Label>
          <Input
            className="mt-1.5 h-10"
            value={current.title || ""}
            onChange={(e) => updatePage({ title: e.target.value })}
            placeholder="Privacy Policy"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-700">Last updated (display text)</Label>
          <Input
            className="mt-1.5 h-10"
            value={current.lastUpdated || ""}
            onChange={(e) => updatePage({ lastUpdated: e.target.value })}
            placeholder="Last updated: July 2025"
          />
        </div>
      </div>

      <div className="mt-4">
        <Label className="text-sm font-semibold text-slate-700">Page content</Label>
        <div className="text-xs text-slate-500 mt-1 mb-2">
          Supports simple formatting: use <code className="bg-slate-100 px-1 rounded">## Heading</code> for section titles,
          <code className="bg-slate-100 px-1 rounded ml-1">- item</code> for bullets, and a blank line between paragraphs.
        </div>
        <Textarea
          rows={20}
          value={current.content || ""}
          onChange={(e) => updatePage({ content: e.target.value })}
          className="font-mono text-[13px] leading-relaxed"
          placeholder="## Section Heading\nParagraph text here.\n\n- Bullet one\n- Bullet two"
        />
      </div>
    </div>
  );
}

function ArrayEditor({ section, value, onChange }) {
  const items = value || [];
  const updateItem = (i, newItem) => {
    const copy = [...items];
    copy[i] = newItem;
    onChange(copy);
  };
  const addItem = () => {
    const blank = {};
    section.fields.forEach((f) => (blank[f.key] = f.type === "number" ? 0 : ""));
    onChange([...items, blank]);
  };
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const moveItem = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const copy = [...items];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };

  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-slate-50/40">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold text-slate-800">
              <span className="inline-block w-6 h-6 rounded-full bg-[#0a4f47] text-white text-xs text-center leading-6 mr-2">{i + 1}</span>
              {section.itemLabel?.(it) || `Item ${i + 1}`}
            </div>
            <div className="flex items-center gap-1">
              <Button type="button" variant="ghost" size="sm" className="h-8 px-2" disabled={i === 0} onClick={() => moveItem(i, -1)}>↑</Button>
              <Button type="button" variant="ghost" size="sm" className="h-8 px-2" disabled={i === items.length - 1} onClick={() => moveItem(i, 1)}>↓</Button>
              <Button type="button" variant="ghost" size="sm" className="h-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => removeItem(i)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {section.fields.map((f) => (
              <div key={f.key} className={f.type === "textarea" || f.type === "image" ? "md:col-span-2" : ""}>
                <Label className="text-xs font-semibold text-slate-600">{f.label}</Label>
                {f.hint && <div className="text-xs text-slate-400">{f.hint}</div>}
                <div className="mt-1">
                  <FieldInput
                    field={f}
                    value={it[f.key]}
                    onChange={(v) => updateItem(i, { ...it, [f.key]: v })}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" className="gap-1.5" onClick={addItem}>
        <Plus className="w-4 h-4" /> Add new
      </Button>
    </div>
  );
}

// ============================================================================
// Main editor
// ============================================================================
export default function ContentEditor() {
  const { content, reload, defaults } = useContent();
  const [draft, setDraft] = useState(content);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(SECTIONS[0].key);
  const { toast } = useToast();

  useEffect(() => {
    setDraft(content);
  }, [content]);

  const updateSection = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      // Strip "indianStates", "billRanges", "navLinks", "footerLinks", "socials" since editor doesn't touch them - keep current values from draft
      await adminApi.saveContent(draft);
      await reload();
      toast({ title: "Saved", description: "Your website is updated." });
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || e?.message });
    } finally {
      setSaving(false);
    }
  };

  const resetSection = (key) => {
    if (!window.confirm("Reset this section to original defaults?")) return;
    setDraft((d) => ({ ...d, [key]: defaults[key] }));
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-slate-900">Website Content</h1>
          <p className="text-slate-500 text-sm">Edit any text or image — changes go live instantly after saving.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={saving} className="bg-[#0a4f47] hover:bg-[#0e6e63] text-white gap-1.5 h-10 px-5">
            {saving ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving</>) : (<><Save className="w-4 h-4" /> Save changes</>)}
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="border border-slate-200 rounded-xl bg-white p-2 mb-5 overflow-x-auto">
          <TabsList className="bg-transparent flex flex-wrap gap-1 h-auto">
            {SECTIONS.map((s) => (
              <TabsTrigger
                key={s.key}
                value={s.key}
                className="data-[state=active]:bg-[#0a4f47] data-[state=active]:text-white px-3 py-2 text-sm rounded-md whitespace-nowrap"
              >
                {s.title}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {SECTIONS.map((s) => (
          <TabsContent key={s.key} value={s.key} className="mt-0">
            <div className="bg-white border border-slate-200 rounded-xl p-5 lg:p-6">
              <div className="flex items-start justify-between mb-5 gap-3">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{s.title}</h2>
                  <p className="text-sm text-slate-500 mt-0.5">{s.desc}</p>
                </div>
                <Button variant="outline" size="sm" className="gap-1.5 flex-shrink-0" onClick={() => resetSection(s.key)}>
                  <RotateCcw className="w-3.5 h-3.5" /> Reset defaults
                </Button>
              </div>
              {s.kind === "object" ? (
                <ObjectEditor section={s} value={draft[s.key]} onChange={(v) => updateSection(s.key, v)} />
              ) : s.kind === "legalPages" ? (
                <LegalPagesEditor value={draft[s.key] || {}} onChange={(v) => updateSection(s.key, v)} />
              ) : (
                <ArrayEditor section={s} value={draft[s.key]} onChange={(v) => updateSection(s.key, v)} />
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white gap-1.5 h-11 px-6">
          {saving ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving</>) : (<><Save className="w-4 h-4" /> Save all changes</>)}
        </Button>
      </div>
    </div>
  );
}
